import { Typography, makeStyles, Grid } from '@material-ui/core'
import useTranslate from '../../../i18n/useTranslate'
import EmptyData from '../officer/EmptyData'
import _ from 'lodash'
import { VictoryAxis, VictoryBar, VictoryChart, VictoryLabel, VictoryTooltip, VictoryVoronoiContainer } from 'victory'
import { useEffect, useState } from 'react'
import { Pagination } from '@material-ui/lab'

type AverageFacultyType = {
    label: {
        th: string
        en: string
    }
    id: number
    value: number
}

type AverageFacultyComponentProps = {
    data: AverageFacultyType[]
    onClick: (facultyID: number, faculty: string) => void
}

function AverageFaculty({ data, onClick }: AverageFacultyComponentProps) {
    const translate = useTranslate()
    const classes = useStyles()
    const lang = translate.languageStore.currentLanguage as 'th' | 'en'
    const [page, setPage] = useState<number>(0)
    const pageCount = 10

    const [projectList, setProjectList] = useState<AverageFacultyType[]>([])
    const [projectListGraph, setProjectListGraph] = useState<AverageFacultyType[]>([])

    useEffect(() => {
        setProjectList(data)
    }, [data])

    useEffect(() => {
        const listData = [...projectList].slice(page * pageCount, (page + 1) * pageCount)
        setProjectListGraph(listData)
    }, [projectList, page])
    return (
        <>
            <Grid container justify="space-between">
                <Typography className={classes.header}>{translate.get('UM.DASHBOARD.AVERAGE_FACULTY')} </Typography>

                <Grid item justify="flex-end">
                    <Pagination
                        color="primary"
                        variant="outlined"
                        shape="rounded"
                        count={Math.ceil(projectList.length / pageCount)}
                        page={page + 1}
                        onChange={(event, value) => {
                            setPage(value - 1)
                            onClick(0, '')
                        }}
                    />
                </Grid>
            </Grid>

            <div className={classes.chart}>
                <EmptyData isEmpty={_.isEmpty(data)}>
                    <VictoryChart
                        horizontal
                        domainPadding={{ x: 15, y: 10 }}
                        padding={{ bottom: 16, left: 100, right: 32, top: 32 }}
                        width={700}
                        height={250}
                        containerComponent={
                            <VictoryVoronoiContainer
                                labels={() => '.'}
                                mouseFollowTooltips={true}
                                voronoiDimension={'x'}
                                activateLabels={true}
                                labelComponent={
                                    <VictoryTooltip
                                        flyoutComponent={
                                            <CustomLabel renderInPortal={true} dataBar={projectListGraph} />
                                        }
                                    />
                                }
                            />
                        }
                    >
                        <VictoryAxis
                            label={() => ''}
                            standalone={false}
                            axisLabelComponent={
                                <VictoryLabel
                                    angle={0}
                                    dx={25}
                                    dy={-105}
                                    text={translate.get('UM.FIRST_TIME.FACILITY.LABEL')}
                                />
                            }
                            tickFormat={(tick) => {
                                return tick.length > 24 ? `${tick.slice(0, 21)}...` : tick
                            }}
                            style={{
                                axisLabel: {
                                    fontSize: '8px',
                                    fill: '#B9B9B9',
                                    fontFamily: 'Prompt',
                                },
                                axis: { stroke: '#BDBDBD' },
                                tickLabels: {
                                    fontSize: '8px',
                                    padding: 4,
                                    fill: '#3B4859',
                                    fontFamily: 'Prompt',
                                },
                            }}
                            // tickComponent={}
                        />

                        <VictoryAxis
                            tickFormat={(tick) =>
                                Number.isInteger(tick) ? tick : tick.toString().includes('e') ? null : tick.toFixed(1)
                            }
                            dependentAxis
                            standalone={false}
                            label={() => ''}
                            axisLabelComponent={
                                <VictoryLabel dx={330} dy={-30} text={translate.get('UM.DASHBOARD.UNIT_DAY')} />
                            }
                            tickLabelComponent={<VictoryLabel style={{ fontSize: '8px' }} />}
                            style={{
                                axisLabel: {
                                    fontSize: '8px',
                                    fill: '#B9B9B9',
                                    fontFamily: 'Prompt',
                                },
                                grid: { stroke: '#BDBDBD', strokeWidth: '0.5' },
                                axis: { stroke: '#BDBDBD' },
                                tickLabels: { fontSize: '8px', padding: 5, fill: '#3B4859', fontFamily: 'Prompt' },
                            }}
                        />

                        <VictoryBar
                            standalone={false}
                            sortKey="x"
                            sortOrder="descending"
                            // labelComponent={<VictoryLabel />}
                            style={{
                                data: {
                                    fill: () => {
                                        return `url(#myGradient_3)`
                                    },
                                    width: 15,
                                },
                                labels: { fontSize: '8px' },
                            }}
                            data={projectListGraph.map((item: AverageFacultyType) => {
                                return { x: item.label[lang].toString(), y: item.value }
                            })}
                            labels={({ datum }) =>
                                `${datum.y} ${translate.languageStore.currentLanguage === 'th' ? 'วัน' : 'day(s)'}`
                            }
                            events={[
                                {
                                    target: 'data',
                                    eventHandlers: {
                                        onClick: () => {
                                            return [
                                                {
                                                    target: 'data',
                                                    mutation: ({ datum }) => {
                                                        const facultyObj = projectListGraph.find(
                                                            (facultyObj) => facultyObj.label[lang] === datum.xName,
                                                        )
                                                        onClick(Number(facultyObj?.id), String(facultyObj?.label[lang]))
                                                    },
                                                },
                                            ]
                                        },
                                    },
                                },
                            ]}
                        />
                    </VictoryChart>
                </EmptyData>
            </div>
        </>
    )
}

const CustomLabel = (props: any) => {
    const { x, y, datum, ...other } = props

    const classes = useStyles()

    if (props.center.x > 100) {
        return (
            <g style={{ pointerEvents: 'none' }}>
                <foreignObject
                    x={x - 30}
                    y={y - 10}
                    height="50"
                    width="150"
                    style={{ border: 'none', display: 'inline-block' }}
                >
                    <div className={classes.container}>
                        <Typography className={classes.tooltipLabel}>{datum.xName}</Typography>
                    </div>
                </foreignObject>
            </g>
        )
    } else {
        return (
            <g style={{ pointerEvents: 'none' }}>
                <foreignObject
                    x={100}
                    y={y - 10}
                    height="50"
                    width="150"
                    style={{ border: 'none', display: 'inline-block' }}
                >
                    <div className={classes.container}>
                        <Typography className={classes.tooltipLabel}>{datum.xName}</Typography>
                    </div>
                </foreignObject>
            </g>
        )
    }
}

const useStyles = makeStyles((theme) => ({
    chart: {
        display: 'block',
        padding: 0,
        margin: 0,
    },
    header: {
        margin: '0px 8px',
        fontWeight: 'bold',
    },
    container: {
        margin: '0px',
        display: 'inline-block',
        borderRadius: '4px',
        backgroundColor: '#ffffff',
        boxShadow: `0px 4px 4px ${theme.palette.grey['50']}`,
        verticalAlign: 'middle',
        padding: '8px',
    },
    tooltipLabel: {
        fontSize: '8px',
    },
}))

export default AverageFaculty
